import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import ImageUploadPreview from "../image-upload-preview";
import StepLayout from "../step-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";

const userInfoSchema = z.object({
	name: z.string().min(1, "Name is required"),
	email: z.string().email("Please enter a valid email address"),
});

type UserInfoFormData = z.infer<typeof userInfoSchema>;

export default function StepAboutUser() {
	const { data, setUserInfo, nextStep } = useFeedbackFlow();

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<UserInfoFormData>({
		resolver: zodResolver(userInfoSchema),
		mode: "onChange",
		defaultValues: {
			name: data.userInfo.name,
			email: data.userInfo.email,
		},
	});

	const onSubmit = (formData: UserInfoFormData) => {
		setUserInfo({
			name: formData.name,
			email: formData.email,
		});
		nextStep();
	};

	const handlePhotoChange = (file: File | null, preview: string | null) => {
		setUserInfo({
			photoFile: file,
			photoPreview: preview,
		});
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="h-full">
			<StepLayout
				header={
					<div className="mb-8">
						<h1 className="text-2xl font-semibold text-primary mb-2">Tell us about yourself</h1>
						<p className="text-secondary">This helps us personalize your testimonial</p>
					</div>
				}
				content={
					<div className="space-y-5">
						<Input label="Your name" type="text" placeholder="e.g. John Smith" required error={errors.name?.message} {...register("name")} />

						<Input label="Email address" type="email" placeholder="e.g. john@company.com" required error={errors.email?.message} {...register("email")} />

						<ImageUploadPreview label="Profile photo (optional)" preview={data.userInfo.photoPreview} onChange={handlePhotoChange} />
					</div>
				}
				footer={
					<Button type="submit" disabled={!isValid} variant="brand" className="w-full">
						Continue
					</Button>
				}
			/>
		</form>
	);
}
